import { useCallback, useEffect, useRef } from "react";
import CollectionsGrid from "./CollectionsGrid";
import ShowcaseWall from "./ShowcaseWall";

/**
 * @param {{ activeTab: "showcase" | "collections", onTabChange: (tab: "showcase" | "collections") => void, onOpenLightbox: (list: import("../../hooks/useShowcasePhotos").ShowcasePhoto[], index: number) => void, onOpenCollection: (id: string) => void, initialShowcaseId?: string | null, showcasePhotos?: import("../../hooks/useShowcasePhotos").ShowcasePhoto[], showcaseLoading?: boolean }} props
 */
export default function PhotographyLanding({
  activeTab,
  onTabChange,
  onOpenLightbox,
  onOpenCollection,
  initialShowcaseId = null,
  showcasePhotos = [],
  showcaseLoading = false,
}) {
  const showcaseRef = useRef(null);
  const collectionsRef = useRef(null);
  const isScrollingRef = useRef(false);

  const scrollToSection = useCallback((tab) => {
    const target =
      tab === "collections" ? collectionsRef.current : showcaseRef.current;
    if (!target) return;

    isScrollingRef.current = true;
    const top = window.scrollY + target.getBoundingClientRect().top - 78;
    window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
    onTabChange(tab);
    window.setTimeout(() => {
      isScrollingRef.current = false;
    }, 600);
  }, [onTabChange]);

  useEffect(() => {
    const onScroll = () => {
      if (isScrollingRef.current || !collectionsRef.current) return;
      const collectionsTop =
        window.scrollY + collectionsRef.current.getBoundingClientRect().top;
      const nextTab = window.scrollY + 220 >= collectionsTop ? "collections" : "showcase";
      if (nextTab !== activeTab) onTabChange(nextTab);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [activeTab, onTabChange]);

  useEffect(() => {
    if (!initialShowcaseId) {
      return;
    }

    onTabChange("showcase");
    window.setTimeout(() => {
      if (!showcaseRef.current) {
        return;
      }
      const top = window.scrollY + showcaseRef.current.getBoundingClientRect().top - 78;
      window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
    }, 50);
  }, [initialShowcaseId, onTabChange]);

  return (
    <div className="photo-landing">
      <section className="photo-landing__intro">
        <div className="photo-landing__eyebrow">PHOTOGRAPHY</div>
        <div className="photo-landing__header">
          <h1 className="photo-landing__title">Signature frames & full collections</h1>
          <div className="photo-subnav" role="tablist" aria-label="Photography sections">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "showcase"}
              className={`photo-subnav__btn${activeTab === "showcase" ? " is-active" : ""}`}
              onClick={() => scrollToSection("showcase")}
            >
              SHOWCASE
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "collections"}
              className={`photo-subnav__btn${activeTab === "collections" ? " is-active" : ""}`}
              onClick={() => scrollToSection("collections")}
            >
              COLLECTIONS
            </button>
          </div>
        </div>
      </section>

      <div ref={showcaseRef}>
        <ShowcaseWall
          onOpenLightbox={onOpenLightbox}
          initialShowcaseId={initialShowcaseId}
          photos={showcasePhotos}
          loading={showcaseLoading}
        />
      </div>

      <div ref={collectionsRef}>
        <CollectionsGrid onOpenCollection={onOpenCollection} />
      </div>
    </div>
  );
}
